import { api } from './Api';
import { requirePortalAccess, portalGeneration } from './PortalSession';
import { getOrderDetails } from './DeliveryOperationsPortalService';
import { getDeliveryIssues } from './DeliveryIssues';

// Small shared queue for missing names and on-demand details. Failures are cached
// for this scope lifetime too, preventing a failed row from creating a request loop.
export class DeliveryIssueResources {
  constructor({ businesses, cache, sessionVersion, notify }) {
    Object.assign(this, { cache, sessionVersion, notify });
    this.businesses = new Set(businesses.map((b) => Number(b.id)));
    this.generation = portalGeneration();
    this.names = new Map();
    this.orders = new Map();
    this.histories = new Map();
    this.queue = [];
    this.running = 0;
    this.disposed = false;
  }
  check(issue) {
    requirePortalAccess();
    if (this.disposed || this.generation !== portalGeneration() || !this.businesses.has(Number(issue.business_id))) {
      throw new Error('Alert scope expired');
    }
  }
  enqueue(work) {
    return new Promise((resolve, reject) => {
      this.queue.push({ work, resolve, reject });
      this.pump();
    });
  }
  pump() {
    while (!this.disposed && this.running < 2 && this.queue.length) {
      const { work, resolve, reject } = this.queue.shift();
      this.running += 1;
      Promise.resolve().then(work).then(resolve, reject).finally(() => {
        this.running -= 1;
        this.pump();
      });
    }
  }
  order(issue) {
    this.check(issue);
    const key = `${issue.business_id}:${issue.order_id}`;
    if (!this.orders.has(key)) {
      const promise = this.enqueue(async () => {
        this.check(issue);
        const data = await getOrderDetails(issue.order_id);
        this.check(issue);
        const businessId = data?.businessId ?? data?.business;
        if ((businessId != null && Number(businessId) !== Number(issue.business_id)) ||
            (data?.id != null && Number(data.id) !== Number(issue.order_id))) throw new Error('Order scope mismatch');
        return data;
      });
      this.orders.set(key, promise);
      promise.catch(() => { if (this.orders.get(key) === promise) this.orders.delete(key); });
    }
    return this.orders.get(key);
  }
  history(issue, cursor) {
    this.check(issue);
    const key = `${issue.business_id}:${issue.order_id}:${cursor || ''}`;
    if (!this.histories.has(key)) {
      const promise = this.enqueue(async () => {
        this.check(issue);
        const page = await getDeliveryIssues({ businessId: issue.business_id, orderId: issue.order_id, cursor });
        this.check(issue);
        return page;
      });
      this.histories.set(key, promise);
      promise.catch(() => { if (this.histories.get(key) === promise) this.histories.delete(key); });
    }
    return this.histories.get(key);
  }
  name(issue) {
    const activeOrders = this.cache.getQueryData(['active-orders', this.sessionVersion]) || [];
    const order = activeOrders.find((o) => Number(o.id) === Number(issue.order_id) &&
      Number(o.businessId) === Number(issue.business_id));
    if (order?.restaurantName) return order.restaurantName;
    // Restaurant IDs are native global IDs; the map cache is session scoped.
    const restaurants = this.cache.getQueryData(['active-restaurants', this.sessionVersion]) || [];
    const restaurant = restaurants.find((r) => Number(r.id) === Number(issue.restaurant_id));
    if (restaurant?.name) return restaurant.name;
    return this.names.get(`${issue.business_id}:${issue.restaurant_id}`) ||
      `Restaurant #${issue.restaurant_id} · ${this.disposed ? 'unavailable' : 'loading'}`;
  }
  resolveNames(issues) {
    issues.forEach((issue) => {
      const key = `${issue.business_id}:${issue.restaurant_id}`;
      if (this.names.has(key)) return;
      const cached = this.name(issue);
      if (!cached.endsWith(' · loading')) { this.names.set(key, cached); return; }
      this.names.set(key, `Restaurant #${issue.restaurant_id} · loading`);
      // One bulk restaurant read, including inactive restaurants, avoids per-row fanout.
      if (!this.restaurantRequest) this.restaurantRequest = this.enqueue(async () => {
        this.check(issue);
        const data = await api.get('/deliveryops/restaurants', undefined, { cache: 'no-store' });
        this.check(issue);
        if (!Array.isArray(data?.results)) throw new Error('Restaurants unavailable');
        return data.results;
      });
      this.restaurantRequest.then((rows) => {
        this.check(issue);
        const row = rows.find((r) => Number(r.id) === Number(issue.restaurant_id));
        if (row?.name) return row.name;
        return this.order(issue).then((order) => order?.restaurantName || order?.restaurant?.name || order?.pickup?.name);
      }, () => this.order(issue).then((order) => order?.restaurantName || order?.restaurant?.name || order?.pickup?.name))
        .then((name) => {
          this.check(issue);
          this.names.set(key, name || `Restaurant #${issue.restaurant_id} · unavailable`);
          this.notify();
        }).catch(() => {
          if (!this.disposed && this.generation === portalGeneration() && this.businesses.has(Number(issue.business_id))) {
            this.names.set(key, `Restaurant #${issue.restaurant_id} · unavailable`);
            this.notify();
          }
        });
    });
  }
  dispose() {
    this.disposed = true;
    this.queue.splice(0).forEach(({ reject }) => reject(new Error('Alert scope expired')));
    this.names.clear();
    this.orders.clear();
    this.histories.clear();
  }
  revoke(businessId) {
    this.businesses.delete(Number(businessId));
    [this.names, this.orders, this.histories].forEach((map) => {
      Array.from(map.keys()).forEach((key) => { if (key.startsWith(`${businessId}:`)) map.delete(key); });
    });
  }
}
